import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Building2, Plus, Save, Trash2, Loader2, CheckCircle2, XCircle, Search,
  ArrowUpDown, GripVertical, Shield, Tag, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

const GestioneCompagnie = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/auto-quotes/companies/list');
      setCompanies(res.data.companies || []);
    } catch { toast.error('Errore caricamento compagnie'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateCompany = async (companyId, field, value) => {
    const updated = companies.map(c => c.id === companyId ? { ...c, [field]: value } : c);
    setCompanies(updated);

    setSaving(companyId);
    try {
      await api.put(`/auto-quotes/companies/${companyId}`, { [field]: value });
    } catch { toast.error('Errore salvataggio'); load(); }
    finally { setSaving(null); }
  };

  const addCompany = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await api.post('/auto-quotes/companies/add', { name: newName.trim() });
      if (res.data.success) {
        toast.success(`${newName} aggiunta`);
        setNewName('');
        load();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setAdding(false); }
  };

  const deleteCompany = async (companyId, name) => {
    if (!window.confirm(`Eliminare ${name}?`)) return;
    try {
      await api.delete(`/auto-quotes/companies/${companyId}`);
      toast.success('Eliminata');
      load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const filtered = search
    ? companies.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : companies;

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="gestione-compagnie">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Gestione Compagnie</h2>
          <p className="text-xs text-slate-500">{companies.length} compagnie registrate - Configura quali puoi utilizzare come intermediario</p>
        </div>
      </div>

      {/* Add new company */}
      <div className="flex gap-2 mb-4">
        <Input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="Nome nuova compagnia..."
          className="h-9 text-sm flex-1 max-w-xs"
          data-testid="new-company-input"
          onKeyDown={e => e.key === 'Enter' && addCompany()}
        />
        <Button onClick={addCompany} disabled={adding || !newName.trim()} className="h-9 text-xs bg-blue-600 hover:bg-blue-700" data-testid="add-company-btn">
          {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          <span className="ml-1">Aggiungi</span>
        </Button>
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca..." className="h-9 text-sm pl-8 w-48" />
        </div>
      </div>

      {/* Companies table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="companies-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 w-8">#</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500">Compagnia</th>
                <th className="text-center px-4 py-2.5 text-[11px] font-semibold text-slate-500">Utilizzabile</th>
                <th className="text-center px-4 py-2.5 text-[11px] font-semibold text-slate-500">Convenzione</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500">Nome Convenzione</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500">Codice Sconto</th>
                <th className="text-center px-4 py-2.5 text-[11px] font-semibold text-slate-500 w-20">Priorita</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500">Note</th>
                <th className="px-3 py-2.5 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((c, idx) => (
                <tr key={c.id} className={`transition-colors ${!c.enabled_for_intermediary ? 'bg-slate-50/50 opacity-60' : 'hover:bg-blue-50/30'}`}>
                  <td className="px-4 py-2 text-xs text-slate-400">{idx + 1}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className={c.enabled_for_intermediary ? 'text-blue-500' : 'text-slate-300'} />
                      <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                      {saving === c.id && <Loader2 size={10} className="animate-spin text-blue-400" />}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => updateCompany(c.id, 'enabled_for_intermediary', !c.enabled_for_intermediary)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${
                        c.enabled_for_intermediary
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : 'bg-red-100 text-red-600 hover:bg-red-200'
                      }`}
                      data-testid={`toggle-usable-${c.code || c.id}`}
                    >
                      {c.enabled_for_intermediary ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                      {c.enabled_for_intermediary ? 'Si' : 'No'}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => updateCompany(c.id, 'has_convention', !c.has_convention)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${
                        c.has_convention
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      }`}
                      data-testid={`toggle-convention-${c.code || c.id}`}
                    >
                      {c.has_convention ? <Tag size={10} /> : null}
                      {c.has_convention ? 'Si' : 'No'}
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={c.convention_name || ''}
                      onChange={e => {
                        setCompanies(prev => prev.map(x => x.id === c.id ? { ...x, convention_name: e.target.value } : x));
                      }}
                      onBlur={e => updateCompany(c.id, 'convention_name', e.target.value)}
                      placeholder="-"
                      className="h-7 text-xs border-0 bg-transparent px-1 focus:bg-white focus:border focus:border-slate-200"
                      disabled={!c.has_convention}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={c.discount_code || ''}
                      onChange={e => {
                        setCompanies(prev => prev.map(x => x.id === c.id ? { ...x, discount_code: e.target.value } : x));
                      }}
                      onBlur={e => updateCompany(c.id, 'discount_code', e.target.value)}
                      placeholder="-"
                      className="h-7 text-xs border-0 bg-transparent px-1 focus:bg-white focus:border focus:border-slate-200 font-mono"
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <Input
                      type="number"
                      value={c.priority_order || ''}
                      onChange={e => {
                        setCompanies(prev => prev.map(x => x.id === c.id ? { ...x, priority_order: parseInt(e.target.value) || 99 } : x));
                      }}
                      onBlur={e => updateCompany(c.id, 'priority_order', parseInt(e.target.value) || 99)}
                      className="h-7 text-xs border-0 bg-transparent px-1 text-center w-14 mx-auto focus:bg-white focus:border focus:border-slate-200"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={c.notes || ''}
                      onChange={e => {
                        setCompanies(prev => prev.map(x => x.id === c.id ? { ...x, notes: e.target.value } : x));
                      }}
                      onBlur={e => updateCompany(c.id, 'notes', e.target.value)}
                      placeholder="-"
                      className="h-7 text-xs border-0 bg-transparent px-1 focus:bg-white focus:border focus:border-slate-200"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => deleteCompany(c.id, c.name)}>
                      <Trash2 size={13} />
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400 text-sm">Nessuna compagnia trovata</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 mt-3">
        Le compagnie qui configurate vengono incrociate con i risultati reali di Preventivass.
        Solo quelle marcate come "Utilizzabile" verranno evidenziate nei preventivi.
      </p>
    </div>
  );
};

export default GestioneCompagnie;
